<template>
  <div>
    <b-breadcrumb :items="breadcrumbItems"></b-breadcrumb>

    <div class="row">
      <div class="col-sm-6">
        <h4 qid="users-page-title" class="float-left">
          {{ $t('administration.users.title') }}
        </h4>
      </div>
      <div class="col-sm-6 text-right">
        <button class="btn btn-secondary-dark float-right ml-3 pl-3 pr-3"
                @click="filterOpened = !filterOpened"
                qid="users-filter-button"
        >
          <font-awesome-icon icon="filter"/>
        </button>
        <router-link
                to="/administration/users/create"
                class="btn btn-success float-right ml-3"
                qid="administration-users-create-button"
        >
          {{ $t('administration.users.create_button') }}
        </router-link>
        <b-form-input
                type="text"
                v-model="keywordValue"
                class="float-right mw-300 search-bar"
                v-on:input="debounceInput"
                :placeholder="$t('maintenance.users.search_placeholder')"
                qid="users-search-input"
        ></b-form-input>
      </div>
    </div>

    <div class="row" :class="{'table-with-filters': filterOpened}">
      <div class="col-sm-10 table-container">
        <div class="card">
          <table class="table table-responsive-md">
            <thead>
              <tr>
                <th width="100"></th>
                <th scope="col"
                    class="cursor-pointer"
                    @click="selectSort('name')"
                    qid="users-sort-name"
                >
                  {{ $t('administration.users.create.name_label') }}
                  <span class="ml-2">
                      <font-awesome-icon class="text-muted" icon="sort" v-if="selectedSort !== 'name'"/>
                      <font-awesome-icon icon="caret-down" v-if="selectedDirection === 'desc' && selectedSort === 'name'"/>
                      <font-awesome-icon icon="caret-up" v-if="selectedDirection === 'asc' && selectedSort === 'name'"/>
                    </span>
                </th>
                <th scope="col"
                    class="cursor-pointer"
                    @click="selectSort('email')"
                    qid="users-sort-email"
                >
                  {{ $t('administration.users.create.email_label') }}
                  <span class="ml-2">
                      <font-awesome-icon class="text-muted" icon="sort" v-if="selectedSort !== 'email'"/>
                      <font-awesome-icon icon="caret-down" v-if="selectedDirection === 'desc' && selectedSort === 'email'"/>
                      <font-awesome-icon icon="caret-up" v-if="selectedDirection === 'asc' && selectedSort === 'email'"/>
                    </span>
                </th>
                <th scope="col"
                    class="cursor-pointer"
                    @click="selectSort('upn_email')"
                    qid="users-sort-email"
                >
                  {{ $t('administration.users.create.upn_email_label') }}
                  <span class="ml-2">
                      <font-awesome-icon class="text-muted" icon="sort" v-if="selectedSort !== 'email'"/>
                      <font-awesome-icon icon="caret-down" v-if="selectedDirection === 'desc' && selectedSort === 'email'"/>
                      <font-awesome-icon icon="caret-up" v-if="selectedDirection === 'asc' && selectedSort === 'email'"/>
                    </span>
                </th>
                <th scope="col">
                  {{ $t('administration.users.create.phone_label') }}
                </th>
                <th scope="col" class="text-center">
                  {{ $t('maintenance.users.table.authentication') }}
                </th>
                <th width="100"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="object in tableItems">
                <td class="avatar-table-block">
                  <img v-if="object.avatar" v-bind:src="object.avatar"
                       class="rounded-circle"
                       width="50"
                       height="50"
                       qid="profile-photo"
                  >
                  <img v-if="!object.avatar" src="@/assets/avatar.png"
                       class="rounded-circle"
                       width="50"
                       height="50"
                       qid="profile-photo"
                  >
                </td>
                <td>
                  <router-link
                      :to="{ name: 'administration.userView', params: { slug: object.slug }}"
                      qid="administration-users-name"
                  >
                    {{object.name}}
                  </router-link>
                </td>
                <td>{{object.email}}</td>
                <td>{{object.upn_email}}</td>
                <td>{{object.phone}}</td>
                <td class="text-center">
                  <span v-if="object.authentication == 'Microsoft'" class="fs-1-5">
                    <font-awesome-icon :icon="['fab', 'microsoft']"/>
                  </span>
                  <span v-if="object.authentication == 'Google'" class="fs-1-5">
                    <font-awesome-icon :icon="['fab', 'google']"/>
                  </span>
                  <span v-if="!object.authentication || object.authentication == 'Application'">
                    <img src="@/assets/logo_new.png"
                         class="rounded-circle"
                         width="25"
                         height="25"
                         qid="application-logo"
                    >
                  </span>
                </td>
                <td>
                  <span class="text-muted mr-3 cursor-pointer" qid="administration-users-edit-button" @click="showEditUserModal(object)">
                    <font-awesome-icon icon="pen"/>
                  </span>
                  <span class="text-muted cursor-pointer" qid="administration-users-delete-button" @click="showConfirmDeleteModal(object)">
                    <font-awesome-icon icon="trash-alt" />
                  </span>
                </td>
              </tr>
              <tr v-if="!loading && !tableItems.length" qid="no-results-row" class="text-center">
                <td colspan="10">
                  <h5 class="mt-3">
                    {{ $t('system.no_results') }}
                  </h5>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="mt-3 pagination row">
          <div class="col-md-6">
            <div class="row">
              <div class="float-left">
                <span class="text-muted pagination-counter">
                  {{ $t('pagination.entries_per_page') }}:
                </span>
              </div>
              <div class="float-left">
                <multiselect
                    v-model="perPage"
                    :options="perPageOptions"
                    track-by="value"
                    label="value"
                    select-label="✔"
                    selected-label="✔"
                    deselect-label="x"
                    qid="create-org-form-organization-level"
                    @select="perPageChanged"
                    :placeholder="selectedPerPage"
                    @remove="perPageRemoved"
                ></multiselect>
              </div>
            </div>
          </div>
          <div class="col-md-6">
            <b-pagination
                class="float-right"
                v-model="currentPage"
                :total-rows="rows"
                :per-page="selectedPerPage"
                aria-controls="my-table"
                @change="pageChanged"
                limit="1"
            >
              <span class="text-muted" slot="first-text"><font-awesome-icon icon="angle-double-left"/></span>
              <span class="text-muted" slot="prev-text"><font-awesome-icon icon="angle-left"/></span>
              <span class="text-muted" slot="next-text"><font-awesome-icon icon="angle-right"/></span>
              <span class="text-muted" slot="last-text"><font-awesome-icon icon="angle-double-right"/></span>
              <div slot="ellipsis-text">
                <b-spinner small type="grow"></b-spinner>
                <b-spinner small type="grow"></b-spinner>
                <b-spinner small type="grow"></b-spinner>
              </div>
              <span class="text-muted" slot="page" slot-scope="{ page, active }">
                <span v-if="active">{{getPerPage}}</span>
              </span>
            </b-pagination>
          </div>
        </div>
      </div>

      <div class="col-sm-2 filter-container">
        <div class="card">
          <div class="card-body">

            <a href="#"
               @click="resetFilters"
               class="text-right"
               qid="users-filter-reset"
            >
              {{ $t('system.reset_filters') }}
            </a>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete user confirm modal template -->
    <b-modal ref="delete-user" hide-footer>
      <div class="d-block text-center">
        <h4 qid="delete-user-title">
          {{ $t('administration.users.delete.title') }}
        </h4>
      </div>
      <h5 class="text-center">
        {{ $t('administration.users.delete.description') }} {{ itemToDelete.name }}
      </h5>
      <h5 class="text-center">
        {{ $t('administration.users.delete.are_you_sure') }}
      </h5>
      <div class="text-center">
        <b-button class="mt-3"
                  variant="secondary"
                  @click="hideConfirmDeleteModal"
                  qid="delete-user-cancel-button"
        >
          {{ $t('administration.users.delete.cancel') }}
        </b-button>
        <b-button @click="confirmDeleteUser(itemToDelete)"
                  class="mt-3 ml-3"
                  variant="success"
                  qid="delete-user-submit-button"
        >
          {{ $t('administration.users.delete.submit') }}
        </b-button>
      </div>
    </b-modal>


    <!-- Edit user modal template -->
    <b-modal ref="edit-user" hide-footer>
      <div class="d-block text-center">
        <h4 qid="update-user-title">
          {{ $t('administration.users.update.title') }}
        </h4>
      </div>
      <b-alert v-if="error.length"
               show
               variant="danger"
               qid="edit-user-error"
      >
        {{ error }}
      </b-alert>
      <b-form @submit="editUserFormSubmit" qid="edit-user-form">
        <div class="form-container">
          <b-form-group>
            <label qid="edit-user-name-label">
                {{ $t('administration.users.create.name_label') }}
            </label>
            <b-form-input
              qid="edit-user-form-name"
              type="text"
              v-model="userForm.name"
              required
              :placeholder="$t('administration.users.create.name_label')"
            ></b-form-input>
          </b-form-group>
          <b-form-group>
            <label qid="edit-user-phone-label">
                {{ $t('administration.users.create.phone_label') }}
            </label>
            <b-form-input
              type="text"
              qid="edit-user-form-phone"
              v-model="userForm.phone"
              :placeholder="$t('administration.users.create.phone_label')"
            ></b-form-input>
          </b-form-group>
          <b-form-group>
            <label qid="create-user-authentication-label">
              {{ $t('administration.users.create.authentication_label') }}
            </label>
            <b-form-radio-group
              v-model="userForm.authentication"
              :options="radioOptions"
              qid="create-user-form-authentication"
            ></b-form-radio-group>
          </b-form-group>

          <b-form-group v-if="userForm.authentication == 'Microsoft' || userForm.authentication == 'Application'">
            <label qid="create-user-upn-email-label">
              {{ $t('administration.users.create.upn_email_label') }}
            </label>
            <b-form-input
              qid="create-user-form-upn-email"
              type="email"
              v-model="userForm.upn_email"
              :placeholder="userForm.email"
            ></b-form-input>
          </b-form-group>
        </div>
        <div class="text-right">
          <b-button class="mt-3"
                    variant="secondary"
                    @click="hideEditUserModal"
                    qid="edit-user-cancel-button"
          >
            {{ $t('administration.users.create.cancel') }}
          </b-button>
          <b-button type="submit"
                    class="mt-3 ml-3"
                    variant="success"
                    :disabled="buttonDisabled"
                    qid="edit-user-submit-button"
          >
            <span v-if="buttonDisabled" class="mr-1">
              <font-awesome-icon icon="spinner" size="sm" class="fa-spin"/>
            </span>
            {{ $t('administration.users.create.submit') }}
          </b-button>
        </div>
      </b-form>
    </b-modal>

  </div>
</template>

<script>
  import piincHttp from '@/util/configureAxios.js'
  import {config} from '@/config.js'

  export default {
    name: 'Users',
    data: function() {
      return {
        breadcrumbItems: [
          {
            text: this.$t('dashboard.title'),
            to: '/'
          },
          {
            text: this.$t('administration.title'),
            to: '/administration'
          },
          {
            text: this.$t('administration.users.title'),
            active: true
          }
        ],
        tableItems: [],

        // Pagination
        perPageOptions: [
          { name: '10', value: '10' },
          { name: '20', value: '20' },
          { name: '50', value: '50' },
          { name: '100', value: '100' }
        ],
        radioOptions: [
          {text: 'Application', value: 'Application'},
          {text: 'Microsoft', value: 'Microsoft'},
          {text: 'Google', value: 'Google'}
        ],
        perPage: '',
        selectedPerPage: "10",
        currentPage: 1,
        totalRows: 0,
        selectedSort: '',
        selectedDirection: '',
        filterOpened: false,
        keywordValue: '',
        itemToDelete: {},
        loading: true,
        userForm: {
          slug: '',
          name: '',
          phone: '',
          authentication: '',
          upn_email: ''
        },
        error: '',
        roles: [],
        buttonDisabled: false,
        filters: {
          role: ''
        },
      }
    },
    computed: {
      rows() {
        // Use computed value for rows because of dynamic update
        return this.totalRows
      },
      getPerPage() {
        let page = this.currentPage;
        let selectedFrom = (page-1) * Number(this.selectedPerPage) + 1;
        let selectedTo = (page-1) * Number(this.selectedPerPage) + Number(this.selectedPerPage);
        let selectedOf = this.totalRows;

        if(selectedTo > selectedOf){
          selectedTo = selectedOf
        }

        return selectedFrom + " - " + selectedTo + " of " + selectedOf
      }
    },
    methods: {
      debounceInput: _.debounce(function() {
        this.loadUsers()
      }, 400),
      loadUsers(page, perPage) {
        // If page is not defined, set page to default 1
        let pageNumber = page ? page : 1;
        let perPageNumber = perPage ? perPage : this.selectedPerPage;
        let self = this;
        piincHttp.get('users', {
          params: {
            page: pageNumber,
            per_page: perPageNumber,
            sort: this.selectedSort !== '' ? this.selectedSort : undefined,
            order: this.selectedDirection !== '' ? this.selectedDirection : undefined,
            keyword: this.keywordValue !== '' ? this.keywordValue : undefined
          }
        }).then(function(response) {
          self.tableItems = response.data.items;
          self.loading = false;
          // Set number of total items
          self.totalRows = response.data.total_count;
        }, function() {});
      },
      resetFilters() {
        this.filters.type = '';
        this.filters.role = '';
        this.debounceInput();
      },
      resetRole() {
        this.selectedRole = []
      },
      selectSort(sort) {
        // If old and new sort are same, switch just direction
        if (sort === this.selectedSort) {
          this.selectedDirection = this.selectedDirection === 'desc' ? 'asc' : 'desc';
        } else {
          // If different sort, set sort and set direction to default
          this.selectedDirection = 'desc'
          this.selectedSort = sort;
        }
        this.loadUsers();
      },
      pageChanged(page) {
        this.loadUsers(page)
      },
      perPageChanged(perPage) {
        let self = this;
        // Load content when page changed listener is triggered
        this.loadUsers(1, perPage.value)
        self.selectedPerPage = perPage.value
      },
      perPageRemoved() {
        let self = this;
        // Load content when page changed listener is triggered
        self.selectedPerPage = self.perPageOptions[0].value
        this.loadUsers(1, self.selectedPerPage)
      },
      confirmDeleteUser(user) {
        let self = this;
        piincHttp.delete('users/' + user.slug).then(function(response) {
          self.hideConfirmDeleteModal();
          self.loadUsers(self.currentPage);
          // Trigger toastr message after modal close
          self.$toastr('success', self.$t('administration.users.delete.success'))
        }, function() {});
      },
      editUserFormSubmit(evt) {
        this.buttonDisabled = true;
        // Keep the reference in a variable
        // to use it inside function called when promise returned
        let self = this;
        evt.preventDefault();

        if (this.userForm.authentication != 'Microsoft' && this.userForm.authentication != 'Application') {
          this.userForm.upn_email = ''
        }

        piincHttp.put('users/' + this.userForm.slug, this.userForm).then(function(response) {
          self.hideEditUserModal();
          self.loadUsers(self.currentPage);
          self.buttonDisabled = false;
          self.$toastr('success', self.$t('administration.users.update.success'))
        }, function(error) {
          if (error.status !== 200) {
            self.buttonDisabled = false;
            self.error = error.data.message;
          }
        })
      },
      showEditUserModal(object) {
        this.userForm.slug = object.slug;
        this.userForm.name = object.name;
        this.userForm.phone = object.phone;
        this.userForm.upn_email = object.upn_email;
        this.userForm.email = object.email;
        this.userForm.authentication = object.authentication;
        if (!object.authentication) {
          this.userForm.authentication = 'Application'
        }
        this.$refs['edit-user'].show()
      },
      hideEditUserModal() {
        this.$refs['edit-user'].hide()
      },
      showConfirmDeleteModal(object) {
        this.itemToDelete = object
        this.$refs['delete-user'].show()
      },
      hideConfirmDeleteModal() {
        this.itemToDelete = {};
        this.$refs['delete-user'].hide()
      },

    },
    components: {

    },
    created () {
      this.loadUsers();
    }
  }
</script>
